import { useFromInput } from '@ui/features/Trade/hooks/useFromInput';
import { usePayTokenPriceLatest } from '@ui/features/Trade/hooks/usePayTokenPriceLatest';
import { useTradingPair } from '@ui/features/Trade/store/useTradingPair';
import { getOpenPositionFee } from '@ui/utils/trade/getOpenPositionFee';
import { isNil } from 'lodash-es';
import { useMemo } from 'react';

import { usePositionSize } from '../hooks/usePositionSize';
import { useCollateralToken } from '../store/useCollateralToken';

/**
 *
 * openPositionFee = qty * entryPrice * openPositionFeeP
 *
 * positionSize = qty * entryPrice
 *
 * positionSize = leverage * (fromUsd - executionFeeUsd - feeConfigBasisPoints * positionSize)
 */
export const useCalcOpenPositionFee = () => {
  const currentTradePairInfo = useTradingPair((s) => s.useTradingPair());
  const openPositionFeeP = currentTradePairInfo?.feeConfig?.openFeeP;
  const lvTokenDiscountP = currentTradePairInfo?.feeConfig?.lvTokenDiscountP;
  const inputUsd = useFromInput((s) => s.input).value.usd; // fromUsd
  const positionSize = usePositionSize({ inputUsd, isBeforeFee: true });
  const payToken = useCollateralToken((s) => s.useCollateralTokenValue());
  const payTokenPrice = usePayTokenPriceLatest(payToken);

  const openPositionFeeInUsd = useMemo(() => {
    if (isNil(positionSize)) {
      return undefined;
    }

    return getOpenPositionFee({
      openFeeP: openPositionFeeP,
      lvTokenDiscountP: lvTokenDiscountP,
      isPayLVToken: payToken?.address.toLowerCase() === payToken?.lvTokenAddress.toLowerCase(),
      positionSize,
    });
  }, [openPositionFeeP, lvTokenDiscountP, payToken?.address, payToken?.lvTokenAddress, positionSize?.toString()]);

  const openPositionFeeInToken = useMemo(() => {
    if (isNil(openPositionFeeInUsd) || isNil(payTokenPrice)) {
      return undefined;
    }

    return {
      base: openPositionFeeInUsd.base.div(payTokenPrice),
      final: openPositionFeeInUsd.final.div(payTokenPrice),
    };
  }, [openPositionFeeInUsd, payTokenPrice?.toString()]);

  return useMemo(() => {
    if (
      isNil(currentTradePairInfo) ||
      isNil(openPositionFeeP) ||
      isNil(openPositionFeeInUsd) ||
      isNil(openPositionFeeInToken)
    ) {
      return undefined;
    }

    return {
      value: openPositionFeeInToken,
      usd: openPositionFeeInUsd,
    };
  }, [
    openPositionFeeInToken,
    openPositionFeeInUsd,
    openPositionFeeP,
    currentTradePairInfo,
  ]);
};